import { useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent, type ReactNode } from 'react'
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Bug, ChevronRight, GripVertical, ListStart, Route, SkipForward } from 'lucide-react'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import type { TestSessionBug, TestSessionJourneyRun } from '../data/projectTypes'
import { groupTestSessionBugsByJourneyRun, JOURNEY_STATE_GROUPS, groupTestSessionJourneyRunsByState } from '../lib/journeyState'
import { pauseTooltip, type ProjectPause } from '../lib/projectPause'
import {
  useReorderJourneyQueueMutation,
  useRunQueuedJourneysNextMutation,
  useSkipQueuedJourneysMutation,
} from '../services/projectDataApi'
import StatusIndicator from './StatusIndicator'
import StatusBadge from './StatusBadge'
import { SEVERITIES } from './bugSeverity'
import { Link } from './nav'
import './JourneyGroupedList.css'

const SEVERITY_RANK: Record<string, number> = Object.fromEntries(SEVERITIES.map((severity, index) => [severity, index]))

function QueueActionButtons({ disabled, menuItems = false, onRunNext, onSkip }: {
  disabled: boolean
  menuItems?: boolean
  onRunNext: () => void
  onSkip: () => void
}) {
  return (
    <>
      <button type="button" role={menuItems ? 'menuitem' : undefined} disabled={disabled} onClick={onRunNext}>
        <ListStart size={15} aria-hidden /> Run next
      </button>
      <button type="button" role={menuItems ? 'menuitem' : undefined} disabled={disabled} onClick={onSkip}>
        <SkipForward size={15} aria-hidden /> Skip for this Test Run
      </button>
    </>
  )
}

function JourneyState({ run, bugsFound = run.bugs_found, showStatus = true, pause = null }: {
  run: TestSessionJourneyRun
  bugsFound?: number
  showStatus?: boolean
  pause?: ProjectPause | null
}) {
  const status = run.status
  return (
    <span className="journey-grouped-list__state">
      {showStatus && (status === 'cancelled'
        ? <span className="journey-grouped-list__skipped"><SkipForward size={12} aria-hidden /> Skipped</span>
        : status
          ? <StatusIndicator status={status} pause={pause} withIcon />
          : <span className="journey-grouped-list__not-run">Not run</span>)}
      {bugsFound > 0 && (
        <span
          className="journey-grouped-list__bug"
          title={`${bugsFound} ${bugsFound === 1 ? 'bug' : 'bugs'} found`}
          aria-label={`${bugsFound} ${bugsFound === 1 ? 'bug' : 'bugs'} found`}
        >
          <Bug size={14} aria-hidden /> {bugsFound}
        </span>
      )}
    </span>
  )
}

export function ReportedBugsList({ bugs, projectId, label, nested = true }: {
  bugs: TestSessionBug[]
  projectId: string
  label: string
  nested?: boolean
}) {
  if (bugs.length === 0) return null
  const sortedBugs = [...bugs].sort((a, b) => (SEVERITY_RANK[a.severity] ?? SEVERITIES.length) - (SEVERITY_RANK[b.severity] ?? SEVERITIES.length))
  return (
    <ul
      className={`journey-grouped-list__bugs${nested ? ' journey-grouped-list__bugs--nested' : ''}`}
      aria-label={label}
    >
      {sortedBugs.map(bug => (
        <li key={bug.id} className="journey-grouped-list__bug-item">
          <Link
            to={`/projects/${projectId}/bugs/${bug.id}`}
            className="journey-grouped-list__bug-link"
            data-testid={`test-run-journey-bug-${bug.id}`}
            data-analytics-event="test-run-detail-open-bug"
          >
            <Bug size={14} aria-hidden className="journey-grouped-list__bug-icon" />
            <StatusBadge status={bug.severity} variant="severity" />
            <span className="journey-grouped-list__bug-title">{bug.title}</span>
            <StatusBadge status={bug.status} variant="bug" />
          </Link>
        </li>
      ))}
    </ul>
  )
}

function JourneyRowBody({ run, onOpen, dragHandle, queuePosition, selected, onSelectedChange, onContextMenu, showStatus, pause, bugsFound }: {
  run: TestSessionJourneyRun
  onOpen: (id: string) => void
  dragHandle?: ReactNode
  queuePosition?: number
  selected?: boolean
  onSelectedChange?: (selected: boolean) => void
  onContextMenu?: (event: ReactMouseEvent<HTMLDivElement>) => void
  showStatus?: boolean
  pause?: ProjectPause | null
  bugsFound?: number
}) {
  const label = run.journey_name || run.goal || 'Journey run'
  const isQueuedJourney = selected != null
  return (
    <div className="journey-grouped-list__row-surface" onContextMenu={onContextMenu}>
      {dragHandle}
      <button
        type="button"
        className={`journey-grouped-list__open${onSelectedChange ? ' journey-grouped-list__open--selectable' : ''}`}
        disabled={!run.journey_id && !isQueuedJourney}
        aria-pressed={onSelectedChange ? Boolean(selected) : undefined}
        title={isQueuedJourney ? 'Click to select. Double-click to open.' : undefined}
        onClick={(event) => {
          if (isQueuedJourney) {
            // The second click in a double-click reports detail=2. Ignore it so the first click's
            // selection remains stable while the double-click opens the journey.
            if (onSelectedChange && event.detail < 2) onSelectedChange(!selected)
            return
          }
          if (run.journey_id) onOpen(run.journey_id)
        }}
        onDoubleClick={() => {
          if (isQueuedJourney && run.journey_id) onOpen(run.journey_id)
        }}
        data-testid={`test-run-journey-row-${run.id}`}
        data-analytics-event={isQueuedJourney ? 'test-run-detail-select-queued-journey' : 'test-run-detail-open-journey'}
      >
        <Route size={16} className="journey-grouped-list__route" aria-hidden />
        {queuePosition != null && (
          <span
            className="journey-grouped-list__queue-position"
            aria-label={`Queue position ${queuePosition}${queuePosition === 1 ? ', runs next' : ''}`}
            title={queuePosition === 1 ? 'Queue position 1 · runs next' : `Queue position ${queuePosition}`}
          >
            {queuePosition}
          </span>
        )}
        <span className="journey-grouped-list__name">{label}</span>
        {run.status === 'queued' && (run.priority ?? 0) < 0 && (
          <span className="journey-grouped-list__priority">Priority</span>
        )}
        <JourneyState run={run} bugsFound={Math.max(run.bugs_found, bugsFound ?? 0)} showStatus={showStatus} pause={pause} />
        {run.journey_id && !isQueuedJourney && <ChevronRight size={16} className="journey-grouped-list__chevron" aria-hidden />}
      </button>
    </div>
  )
}

function JourneyRow({ run, bugs, projectId, onOpen, queuePosition, showStatus, pause }: {
  run: TestSessionJourneyRun
  bugs: TestSessionBug[]
  projectId: string
  onOpen: (id: string) => void
  queuePosition?: number
  showStatus?: boolean
  pause?: ProjectPause | null
}) {
  const label = run.journey_name || run.goal || 'Journey run'
  return (
    <li className="journey-grouped-list__row">
      <JourneyRowBody run={run} onOpen={onOpen} queuePosition={queuePosition} showStatus={showStatus} pause={pause} bugsFound={bugs.length} />
      <ReportedBugsList bugs={bugs} projectId={projectId} label={`Bugs reported by ${label}`} />
    </li>
  )
}

function SortableJourneyRow({ run, bugs, projectId, onOpen, disabled, queuePosition, selected, onSelectedChange, onContextMenu, pause }: {
  run: TestSessionJourneyRun
  bugs: TestSessionBug[]
  projectId: string
  onOpen: (id: string) => void
  disabled: boolean
  queuePosition: number
  selected: boolean
  onSelectedChange: (selected: boolean) => void
  onContextMenu: (event: ReactMouseEvent<HTMLDivElement>) => void
  pause?: ProjectPause | null
}) {
  const taskId = run.task_id!
  const label = run.journey_name || run.goal || 'Journey run'
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: taskId, disabled })
  return (
    <li
      ref={setNodeRef}
      className={`journey-grouped-list__row${selected ? ' journey-grouped-list__row--selected' : ''}${isDragging ? ' journey-grouped-list__row--dragging' : ''}`}
      style={{ transform: CSS.Transform.toString(transform), transition }}
    >
      <JourneyRowBody
        run={run}
        onOpen={onOpen}
        queuePosition={queuePosition}
        onContextMenu={onContextMenu}
        pause={pause}
        bugsFound={bugs.length}
        selected={selected}
        onSelectedChange={disabled ? undefined : onSelectedChange}
        dragHandle={(
          <button
            type="button"
            className="journey-grouped-list__drag"
            aria-label={`Reorder ${label}`}
            title={disabled ? 'Sign in with project write access to reorder the queue' : 'Drag to reorder'}
            disabled={disabled}
            {...attributes}
            {...listeners}
          >
            <GripVertical size={17} aria-hidden />
          </button>
        )}
      />
      <ReportedBugsList bugs={bugs} projectId={projectId} label={`Bugs reported by ${label}`} />
    </li>
  )
}

export default function JourneyGroupedList({ runs, bugs, projectId, canReorder, onOpen, pause = null }: {
  runs: TestSessionJourneyRun[]
  bugs: TestSessionBug[]
  projectId: string
  canReorder: boolean
  onOpen: (id: string) => void
  /** The project's pause: the journey runs it froze are still stored as 'in-progress'. */
  pause?: ProjectPause | null
}) {
  const grouped = useMemo(() => groupTestSessionJourneyRunsByState(runs, bugs), [runs, bugs])
  const bugsByRunId = useMemo(() => groupTestSessionBugsByJourneyRun(bugs, runs), [bugs, runs])
  const serverQueue = grouped.queued
  const serverQueueKey = serverQueue.map(run => `${run.task_id ?? run.id}:${run.queue_rank ?? ''}`).join('|')
  const [queuedJourneys, setQueuedJourneys] = useState(serverQueue)
  const [selectedTaskIds, setSelectedTaskIds] = useState<Set<string>>(new Set())
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null)
  const [toolbarBounds, setToolbarBounds] = useState({ left: 0, width: 0 })
  const [queueError, setQueueError] = useState<string | null>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const reduceMotion = useReducedMotion()
  const [reorderQueue] = useReorderJourneyQueueMutation()
  const [runNext, runNextState] = useRunQueuedJourneysNextMutation()
  const [skip, skipState] = useSkipQueuedJourneysMutation()
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  useEffect(() => {
    setQueuedJourneys(serverQueue)
  }, [serverQueueKey])

  useEffect(() => {
    const queuedTaskIds = new Set(serverQueue.flatMap(run => run.task_id ? [run.task_id] : []))
    setSelectedTaskIds(current => new Set([...current].filter(taskId => queuedTaskIds.has(taskId))))
  }, [serverQueueKey])

  useEffect(() => {
    if (!contextMenu) return
    const close = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setContextMenu(null)
    }
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setContextMenu(null)
    }
    window.addEventListener('mousedown', close)
    window.addEventListener('keydown', closeOnEscape)
    return () => {
      window.removeEventListener('mousedown', close)
      window.removeEventListener('keydown', closeOnEscape)
    }
  }, [contextMenu])

  useEffect(() => {
    if (selectedTaskIds.size === 0) return
    const main = document.getElementById('main-content')
    const updateBounds = () => {
      const rect = main?.getBoundingClientRect()
      setToolbarBounds(rect
        ? { left: rect.left, width: rect.width }
        : { left: 0, width: window.innerWidth })
    }
    updateBounds()
    window.addEventListener('resize', updateBounds)
    const resizeObserver = main && typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(updateBounds)
      : null
    if (main && resizeObserver) resizeObserver.observe(main)
    return () => {
      window.removeEventListener('resize', updateBounds)
      resizeObserver?.disconnect()
    }
  }, [selectedTaskIds.size])

  function setTaskSelected(taskId: string, selected: boolean) {
    setSelectedTaskIds(current => {
      const next = new Set(current)
      if (selected) next.add(taskId)
      else next.delete(taskId)
      return next
    })
  }

  function openContextMenu(event: ReactMouseEvent<HTMLDivElement>, taskId: string) {
    if (!canReorder) return
    event.preventDefault()
    if (!selectedTaskIds.has(taskId)) setSelectedTaskIds(new Set([taskId]))
    setContextMenu({
      x: Math.min(event.clientX, Math.max(8, window.innerWidth - 230)),
      y: Math.min(event.clientY, Math.max(8, window.innerHeight - 118)),
    })
  }

  function selectedIdsInQueueOrder(): string[] {
    return queuedJourneys.flatMap(run => {
      const taskId = run.task_id
      return taskId && selectedTaskIds.has(taskId) ? [taskId] : []
    })
  }

  async function handleRunNext() {
    const taskIds = selectedIdsInQueueOrder()
    if (taskIds.length === 0) return
    const previous = queuedJourneys
    const priorities = [...new Set(previous.map(run => run.priority ?? 0))]
    const next = priorities.flatMap(priority => {
      const group = previous.filter(run => (run.priority ?? 0) === priority)
      return [
        ...group.filter(run => run.task_id && selectedTaskIds.has(run.task_id)),
        ...group.filter(run => !run.task_id || !selectedTaskIds.has(run.task_id)),
      ]
    })
    setQueuedJourneys(next)
    setContextMenu(null)
    setQueueError(null)
    try {
      await runNext({ projectId, taskIds }).unwrap()
      setSelectedTaskIds(new Set())
    } catch {
      setQueuedJourneys(previous)
      setQueueError('The queue changed before those journeys could be moved. The latest order has been restored.')
    }
  }

  async function handleSkip() {
    const taskIds = selectedIdsInQueueOrder()
    if (taskIds.length === 0) return
    const previous = queuedJourneys
    setQueuedJourneys(current => current.filter(run => !run.task_id || !selectedTaskIds.has(run.task_id)))
    setContextMenu(null)
    setQueueError(null)
    try {
      await skip({ projectId, taskIds }).unwrap()
      setSelectedTaskIds(new Set())
    } catch {
      setQueuedJourneys(previous)
      setQueueError('The queue changed before those journeys could be skipped. The latest queue has been restored.')
    }
  }

  async function handleDragEnd(event: DragEndEvent) {
    const activeId = String(event.active.id)
    const overId = event.over ? String(event.over.id) : null
    if (!overId || activeId === overId) return
    const oldIndex = queuedJourneys.findIndex(run => run.task_id === activeId)
    const newIndex = queuedJourneys.findIndex(run => run.task_id === overId)
    if (oldIndex < 0 || newIndex < 0) return

    const activePriority = queuedJourneys[oldIndex]?.priority ?? 0
    const overPriority = queuedJourneys[newIndex]?.priority ?? 0
    if (activePriority !== overPriority) {
      setQueueError('Priority work stays ahead of the normal queue. Reorder within the same priority group.')
      return
    }

    const previous = queuedJourneys
    const next = arrayMove(previous, oldIndex, newIndex)
    setQueuedJourneys(next)
    setQueueError(null)

    const samePriority = next.filter(run => (run.priority ?? 0) === activePriority)
    const movedIndex = samePriority.findIndex(run => run.task_id === activeId)
    const beforeTaskId = samePriority[movedIndex - 1]?.task_id ?? null
    const afterTaskId = samePriority[movedIndex + 1]?.task_id ?? null
    try {
      await reorderQueue({ projectId, taskId: activeId, beforeTaskId, afterTaskId }).unwrap()
    } catch {
      setQueuedJourneys(previous)
      setQueueError('The queue changed while you were reordering it. The latest order has been restored.')
    }
  }

  const queueActionsDisabled = runNextState.isLoading || skipState.isLoading
  const selectionLabel = `${selectedTaskIds.size} selected queued ${selectedTaskIds.size === 1 ? 'journey' : 'journeys'}`

  return (
    <div className="journey-grouped-list" data-testid="test-run-journey-groups">
      {JOURNEY_STATE_GROUPS.map(definition => {
        const items = definition.id === 'queued' ? queuedJourneys : grouped[definition.id]
        if (items.length === 0) return null
        const headingId = `journey-group-${definition.id}`
        // Nothing is being tested under a pause, so the "Running now" heading over the frozen journey
        // runs is the loudest wrong thing on the page. Rename that one group; every other group is a
        // finished state the pause did not change.
        const paused = !!pause && definition.id === 'running'
        const heading = paused ? 'Paused' : definition.label
        const list = (
          <ul className="journey-grouped-list__items" aria-labelledby={headingId}>
            {items.map((run, index) => definition.id === 'queued' && run.task_id ? (
              <SortableJourneyRow
                key={run.id}
                run={run}
                bugs={bugsByRunId.get(run.id) ?? []}
                projectId={projectId}
                onOpen={onOpen}
                disabled={!canReorder}
                queuePosition={index + 1}
                selected={selectedTaskIds.has(run.task_id)}
                onSelectedChange={selected => setTaskSelected(run.task_id!, selected)}
                onContextMenu={event => openContextMenu(event, run.task_id!)}
                pause={pause}
              />
            ) : (
              <JourneyRow
                key={run.id}
                run={run}
                bugs={bugsByRunId.get(run.id) ?? []}
                projectId={projectId}
                onOpen={onOpen}
                queuePosition={definition.id === 'queued' ? index + 1 : undefined}
                showStatus={definition.id !== 'bugs-reported'}
                pause={pause}
              />
            ))}
          </ul>
        )
        return (
          <section
            key={definition.id}
            className={`journey-grouped-list__group journey-grouped-list__group--${definition.id}${paused ? ' journey-grouped-list__group--paused' : ''}`}
          >
            <div className="journey-grouped-list__heading">
              <h3 id={headingId} title={paused ? pauseTooltip(pause) : undefined}>{heading}</h3>
              <span>{items.length}</span>
              {definition.id === 'queued' && <span className="journey-grouped-list__queue-direction">Top runs next</span>}
              {definition.id === 'queued' && canReorder && (
                <button
                  type="button"
                  className="journey-grouped-list__select-all"
                  onClick={() => setSelectedTaskIds(selectedTaskIds.size === items.length
                    ? new Set()
                    : new Set(items.flatMap(run => run.task_id ? [run.task_id] : [])))}
                >
                  {selectedTaskIds.size === items.length ? 'Clear selection' : 'Select all'}
                </button>
              )}
            </div>
            <p className="sr-only">{definition.description}</p>
            {definition.id === 'queued' ? (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={event => void handleDragEnd(event)}>
                <SortableContext items={items.flatMap(run => run.task_id ? [run.task_id] : [])} strategy={verticalListSortingStrategy}>
                  {list}
                </SortableContext>
              </DndContext>
            ) : list}
          </section>
        )
      })}
      {queueError && <p className="journey-grouped-list__error" role="status">{queueError}</p>}
      {contextMenu && selectedTaskIds.size > 0 && (
        <div
          ref={menuRef}
          className="journey-grouped-list__menu"
          role="menu"
          aria-label={`Actions for ${selectionLabel}`}
          style={{ left: contextMenu.x, top: contextMenu.y }}
        >
          <p>{selectedTaskIds.size} selected</p>
          <QueueActionButtons
            disabled={queueActionsDisabled}
            menuItems
            onRunNext={() => void handleRunNext()}
            onSkip={() => void handleSkip()}
          />
        </div>
      )}
      <AnimatePresence>
        {selectedTaskIds.size > 0 && (
          <motion.div
            className="journey-grouped-list__selection-toolbar-layer"
            style={{ left: toolbarBounds.left, width: toolbarBounds.width || '100%' }}
            initial={reduceMotion ? false : { opacity: 0, y: 10, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 8, scale: 0.98 }}
            transition={{ duration: reduceMotion ? 0 : 0.18, ease: [0.165, 0.84, 0.44, 1] }}
          >
            <div
              className="journey-grouped-list__selection-toolbar"
              role="toolbar"
              aria-label={`Actions for ${selectionLabel}`}
              data-testid="queued-journey-selection-toolbar"
            >
              <span className="journey-grouped-list__selection-count">{selectedTaskIds.size} selected</span>
              <QueueActionButtons
                disabled={queueActionsDisabled}
                onRunNext={() => void handleRunNext()}
                onSkip={() => void handleSkip()}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
